import { useCallback } from 'react';

import { BFFConsumerEnum } from '../../constants';

import { useCache } from './useCache';
import { useConsumer } from './useConsumer';
import useOriginUrl from './useOriginUrl';

type BuildURLProps = {
	baseUrl?: string;
};

interface BuildProductShortcutURLProps {
	baseUrl?: string;
}

interface BuildUCMenuItemsURLProps {
	ap?: string;
	d: string;
	locale?: string;
	orgId: string;
	product?: string;
	ro?: string;
	rt?: string;
	subProduct?: string;
	userId?: string;
}

export enum BFFEndpointsEnum {
	AVAILABLE_PRODUCTS = '/gateway/api/app-switcher/api/available-products',
	MENU_ITEMS_V2 = '/gateway/api/app-switcher/api/universal-create/v2/menu-items',
}

interface UseURLBuilderProps {
	cloudId?: string;
	product?: string;
	userId?: string;
}

const getOriginUrl = ({ originUrl }: { originUrl?: string } = {}): string =>
	originUrl || new URL(window.location.href).origin;

export function useURLBuilder({ cloudId, userId, product }: UseURLBuilderProps): {
	buildProductShortcutURL: ({ baseUrl }: BuildProductShortcutURLProps) => string;
	buildUCMenuItemsURL: (props: BuildUCMenuItemsURLProps) => string;
	buildURL: ({ baseUrl }: BuildURLProps) => string;
} {
	const { consumer } = useConsumer();
	const { items: cachedItems } = useCache();
	const originUrl = useOriginUrl();

	const buildUCMenuItemsURL = useCallback(
		(props: BuildUCMenuItemsURLProps): string => {
			const {
				product: productParam,
				orgId,
				userId: userIdParam,
				locale,
				subProduct,
				d,
				ro,
				rt,
				ap,
			} = props;
			const baseUrl = BFFEndpointsEnum.MENU_ITEMS_V2;

			let url: URL;
			try {
				url = new URL(`${getOriginUrl({ originUrl })}${baseUrl}`);
			} catch (e) {
				return '';
			}
			const searchParams = new URLSearchParams();

			// Maintain specific order: tenantid, product, orgid, userid, source, then other params
			const queryParams: Array<[string, string | undefined | null]> = [
				['tenantid', cloudId],
				['product', productParam || product],
				['orgid', orgId],
				['userid', userIdParam || userId],
				['source', consumer],
				['locale', locale],
				['subproduct', subProduct],
				['d', d],
				['ro', ro],
				['rt', rt],
				['ap', ap],
			];

			queryParams.forEach(([key, value]) => {
				if (value) {
					searchParams.append(key, value);
				}
			});

			// Append search params to URL
			url.search = searchParams.toString();

			return url.toString();
		},
		[cloudId, product, userId, consumer, originUrl],
	);

	const buildProductShortcutURL = useCallback(
		({ baseUrl }: BuildProductShortcutURLProps): string => {
			const finalBaseUrl = baseUrl ?? BFFEndpointsEnum.AVAILABLE_PRODUCTS;

			// No point in building a URL if there are no items to fetch and the cache is already populated
			if (cachedItems.length > 0) {
				return '';
			}

			const queryValues = '';

			return `${getOriginUrl({ originUrl })}${finalBaseUrl}?${queryValues}`;
		},
		[cachedItems, originUrl],
	);

	const buildURL = useCallback(
		({ baseUrl }: BuildURLProps): string => {
			let url = '';

			if (baseUrl) {
				url = `${getOriginUrl({ originUrl })}${baseUrl}`;
			}

			// Consumer specific URL building implementations happen here
			switch (consumer) {
				case BFFConsumerEnum.GLOBAL_SHORTCUTS:
					url = buildProductShortcutURL({});
					break;
				case BFFConsumerEnum.GLOBAL_SIDE_NAVIGATION:
					url = buildProductShortcutURL({});
					break;
			}

			if (!url) {
				return url;
			}

			// Append the consumer to the URL
			let formattedUrl: URL;
			try {
				formattedUrl = new URL(url);
			} catch (e) {
				return url;
			}
			const updatedParams = new URLSearchParams(formattedUrl.search);

			if (consumer) {
				updatedParams.append('source', consumer);
			}

			if (cloudId) {
				updatedParams.append('tenantid', cloudId);
			}

			if (userId) {
				updatedParams.append('userid', userId);
			}

			if (product) {
				updatedParams.append('product', product);
			}

			return `${formattedUrl.origin}${formattedUrl.pathname}?${updatedParams}`;
		},
		[buildProductShortcutURL, consumer, cloudId, userId, product, originUrl],
	);

	return {
		buildURL,
		buildProductShortcutURL,
		buildUCMenuItemsURL,
	};
}
